import { useMutation, useQuery } from "@apollo/client";
import AutoAwesome from "@mui/icons-material/AutoAwesome";
import NotificationsActive from "@mui/icons-material/NotificationsActive";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import EncampAutocomplete from "components/Forms/EncampAutocomplete";
import { gql } from "generated-graphql";
import { ScoutTestNotificationScenario } from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { GENERATE_SCOUT_TEST_NOTIFICATION } from "hooks/useNotifications";
import { usePageTitle } from "hooks/usePageTitle";
import { useTenant } from "hooks/useTenant";
import { useEffect, useMemo, useRef, useState } from "react";

import { useOakStream } from "../../../stream";

type TenantOption = { id: string; name: string };

const SEARCH_TENANTS_FOR_NOTIFICATIONS_TEST = gql(`
  query SearchTenantsForNotificationsTest(
    $search: String
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    tenants(search: $search, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
      }
      count
    }
  }
`);

const scenarioOptions = [
  {
    value: ScoutTestNotificationScenario.Epcra_302EmergencyTierIiReport,
    label: "302 emergency Tier II",
  },
  {
    value: ScoutTestNotificationScenario.Epcra_311Initial,
    label: "311 initial",
  },
  {
    value: ScoutTestNotificationScenario.FeatureAnnouncement,
    label: "Feature announcement",
  },
  {
    value: ScoutTestNotificationScenario.EnvironmentalComplianceTask,
    label: "Environmental compliance task",
  },
];

function formatEventCount(count: number) {
  if (count === 1) {
    return "1 event";
  }

  return `${count} events`;
}

export function ScoutNotificationTest() {
  usePageTitle("Notifications test");
  useBreadcrumb({ label: "Notifications test" });
  const alerts = useAlerts();
  const { tenant } = useTenant();
  const { user } = useCurrentUser();
  const [selectedTenant, setSelectedTenant] = useState<TenantOption | null>(null);
  const [tenantSearch, setTenantSearch] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [facilityId, setFacilityId] = useState("");
  const [scenario, setScenario] = useState<ScoutTestNotificationScenario>(
    ScoutTestNotificationScenario.Epcra_302EmergencyTierIiReport
  );
  const [generateScoutTestNotification, generateState] = useMutation(
    GENERATE_SCOUT_TEST_NOTIFICATION
  );
  const effectiveTenantId = selectedTenant?.id ?? "";
  const stream = useOakStream({
    url: "/events/notifications",
    enabled: true,
  });

  // Seed the tenant picker. If the logged-in user has a selected tenant, use
  // that. Otherwise (e.g. staff users browsing without a tenant context), grab
  // the first tenant alphabetically so there's always a sensible default.
  const fallbackTenantsQuery = useQuery(SEARCH_TENANTS_FOR_NOTIFICATIONS_TEST, {
    variables: { pageSize: 1, sort: [{ field: "name", sort: "asc" }] },
    skip: Boolean(selectedTenant) || Boolean(tenant?.id),
    fetchPolicy: "cache-first",
  });

  useEffect(() => {
    if (selectedTenant) {
      return;
    }
    if (tenant?.id && tenant.name) {
      setSelectedTenant({ id: tenant.id, name: tenant.name });
      return;
    }
    const fallback = fallbackTenantsQuery.data?.tenants.items[0];
    if (fallback) {
      setSelectedTenant({ id: fallback.id, name: fallback.name });
    }
  }, [selectedTenant, tenant?.id, tenant?.name, fallbackTenantsQuery.data]);

  // Seed the field with the current user's email once it loads, but only once.
  // Keying the effect on userEmail would re-seed the moment the field is
  // cleared, making it impossible to type a different recipient or leave it
  // blank.
  const hasSeededEmail = useRef(false);
  useEffect(() => {
    if (hasSeededEmail.current || !user?.email) {
      return;
    }

    hasSeededEmail.current = true;
    setUserEmail(user.email);
  }, [user?.email]);

  const lastStreamEvent = useMemo(() => stream.data.at(-1), [stream.data]);

  const tenantSearchVariables = useMemo(
    () => ({
      search: tenantSearch,
      pageSize: 25,
      sort: [{ field: "name" as const, sort: "asc" as const }],
    }),
    [tenantSearch]
  );

  const handleScenarioChange = (event: SelectChangeEvent<ScoutTestNotificationScenario>) => {
    const nextScenario = scenarioOptions.find((option) => option.value === event.target.value);
    if (!nextScenario) {
      return;
    }

    setScenario(nextScenario.value);
  };

  const handleGenerate = async () => {
    await generateScoutTestNotification({
      variables: {
        input: {
          tenantId: effectiveTenantId || null,
          userEmail: userEmail.trim() || null,
          facilityId: facilityId.trim() || null,
          scenario,
        },
      },
    });
  };

  const canGenerate = scenario.length > 0;

  return (
    <Stack spacing={2} py={3} sx={{ px: 3 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <NotificationsActive color="primary" />
        <Stack>
          <Typography variant="h5">Notifications test</Typography>
          <Typography variant="body2" color="text.secondary">
            Generate inbox notifications through the same real-time stream used by the bell.
          </Typography>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Alert severity="warning">
            Temporary staff tool. Use customer tenant IDs and test recipients only.
          </Alert>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <Box sx={{ flex: { xs: "1 1 auto", md: "2 1 0" }, minWidth: 0 }}>
              <EncampAutocomplete<
                { tenants: { items: TenantOption[]; count: number } },
                TenantOption,
                { search: string; pageSize: number; sort: { field: "name"; sort: "asc" }[] }
              >
                query={SEARCH_TENANTS_FOR_NOTIFICATIONS_TEST}
                getItems={(data) => data?.tenants.items ?? []}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : (option as TenantOption).name
                }
                variables={tenantSearchVariables}
                value={selectedTenant}
                onChange={(value) => setSelectedTenant((value as TenantOption | null) ?? null)}
                onInputChange={(value) => setTenantSearch(value)}
                renderInput={(params) => (
                  <TextField {...params} size="small" label="Tenant" fullWidth />
                )}
              />
            </Box>
            <TextField
              label="Target user email"
              value={userEmail}
              onChange={(event) => setUserEmail(event.target.value)}
              size="small"
              sx={{ flex: { xs: "1 1 auto", md: "1 1 0" }, minWidth: 0 }}
            />
          </Stack>
          <TextField
            label="Facility ID"
            value={facilityId}
            onChange={(event) => setFacilityId(event.target.value)}
            size="small"
            fullWidth
            helperText="Optional; first facility in the tenant is used when a tenant is set, otherwise the notification has no facility."
          />
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="flex-start">
            <FormControl size="small" fullWidth>
              <InputLabel id="scout-test-notification-scenario-label">Scenario</InputLabel>
              <Select
                labelId="scout-test-notification-scenario-label"
                label="Scenario"
                value={scenario}
                onChange={handleScenarioChange}
              >
                {scenarioOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              startIcon={<AutoAwesome />}
              variant="contained"
              disabled={!canGenerate || generateState.loading}
              onClick={() => {
                void handleGenerate().catch((err) => {
                  alerts.error("Failed to generate Scout test notification", err);
                });
              }}
              sx={{ minWidth: 220 }}
            >
              Generate notification
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle1" fontWeight={700}>
              Live notification stream
            </Typography>
            <Chip size="small" label={stream.status} />
            <Chip size="small" label={formatEventCount(stream.data.length)} variant="outlined" />
          </Stack>
          {stream.error ? <Alert severity="error">{stream.error.message}</Alert> : null}
          <TextField
            label="Last stream event"
            value={lastStreamEvent ? JSON.stringify(lastStreamEvent, null, 2) : "No events yet"}
            multiline
            minRows={5}
            size="small"
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
          />
        </Stack>
      </Paper>
    </Stack>
  );
}
